import CreateAccountPopUp from '@/components/main/CreateAccountPopUp'
import { Button } from '@/components/ui/button'
import LoadingSpinner from '@/components/ui/loading'
import { useAuthContext, useLayoutPreference } from '@/context'
import { CommandPaletteProvider } from '@/context/CommandPaletteContext'
import FileContextProvider from '@/context/file'
import { SequenceContextProvider } from '@/context/sequence'
import { OnboardingModal, useOnboarding } from '@/modules/onboarding'
import { ChevronLeft, ChevronRight, Sparkle } from 'lucide-react'
import { useEffect } from 'react'
import { Outlet, matchPath, useLocation, useNavigate } from 'react-router-dom'
import Header from './components/Header'
import Sidebar from './components/Sidebar'
import AgentModal from '@/modules/agent/AgentModal'

const pathsToAutoCollapseSidebar = [
    '/dashboard/:type/file/:id',
    '/dashboard/mood-boards/default',
    '/dashboard/mood-boards/custom/:id',
]

const DashboardLayout = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { user } = useAuthContext()
    const { isCollapsed, setIsCollapsed } = useLayoutPreference()
    const { showOnboarding, userName, onComplete, onClose, onSkipAll, onboardingSteps, isMobileConsumer } =
        useOnboarding()

    const toggleNavbar = () => {
        setIsCollapsed(!isCollapsed)
    }

    const isDemoUser = !user?.active_account && typeof user?.active_account === 'boolean'

    const pagesToHideHeader =
        location.pathname.includes('/dashboard/admin/flows/') ||
        location.pathname.includes('/file') ||
        location.pathname.includes('/dashboard/admin/sequences/')

    const formsPage = location.pathname.includes('/dashboard/forms/form')


    useEffect(() => {
        if (isCollapsed) {
            document.body.classList.remove('sidebar-open')
            document.body.classList.add('sidebar-collapsed')
        } else {
            document.body.classList.remove('sidebar-collapsed')
            document.body.classList.add('sidebar-open')
        }

        return () => {
            document.body.classList.remove('sidebar-collapsed')
            document.body.classList.remove('sidebar-open')
        }
    }, [isCollapsed])

    useEffect(() => {
        const matchesRouteToCollapse = pathsToAutoCollapseSidebar.some(path => matchPath(path, location.pathname))
        if (matchesRouteToCollapse) {
            setIsCollapsed(true)
        } else {
            setIsCollapsed(false)
        }
    }, [location.pathname, setIsCollapsed])

    // Mobile-consumer route guard (defense in depth beyond sidebar hiding)
    useEffect(() => {
        if (!user) return
        if (user.experience_variant !== 'mobile_consumer') return

        // Mobile consumer: Campaigns entry should land on Utilities, not the agency dashboard.
        if (location.pathname === '/dashboard/campaigns' || location.pathname === '/dashboard/campaigns/') {
            navigate('/dashboard/campaigns/utilities', { replace: true })
            return
        }

        const blockedPrefixes = [
            '/dashboard/2hq',
            '/dashboard/visual-engine',
            '/dashboard/campaigns/visual-studios',
            '/dashboard/persuasion-lab',
            '/dashboard/content-sheets',
            '/dashboard/datasets',
            '/dashboard/flows',
        ]

        const pathname = location.pathname
        const isBlockedByPrefix = blockedPrefixes.some(prefix => pathname.startsWith(prefix))

        // Campaigns: allow the dashboard and the single-segment overview page (/dashboard/campaigns/:id).
        // Block deeper campaign routes until we ship mobile-only campaign flows.
        const isCampaignsDeepRoute = (() => {
            if (!pathname.startsWith('/dashboard/campaigns/')) return false
            // Mobile consumer: allow Utilities deep routes (e.g. Deliverable Studio).
            if (pathname.startsWith('/dashboard/campaigns/utilities/')) return false
            // allow /dashboard/campaigns/:id (single segment)
            return !/^\/dashboard\/campaigns\/[^/]+\/?$/.test(pathname)
        })()

        const isBlocked = isBlockedByPrefix || isCampaignsDeepRoute

        if (isBlocked) {
            navigate('/dashboard/home', { replace: true })
        }
    }, [user, location.pathname, navigate])

    if (!user) {
        return <LoadingSpinner />
    }

    return (
        <CommandPaletteProvider>
            <OnboardingModal
                    isOpen={showOnboarding}
                    userName={userName}
                    steps={onboardingSteps}
                    onClose={onClose}
                    onComplete={onComplete}
                    onSkipAll={isMobileConsumer ? onSkipAll : undefined}
                    finalCtaLabel={isMobileConsumer ? 'Go to dashboard' : 'Get started'}
                />
                <div className='flex min-h-screen overflow-y-hidden bg-gray-50'>
                    {user?.active_account === false && <CreateAccountPopUp />}

                    {/* Collapsible Navbar */}
                    <div
                        className={`hidden md:block transform top-0 left-0 bg-surface-muted h-full fixed z-40 transition-all ease-in-out duration-300 border border-stroke-base rounded-2xl ${
                            isCollapsed ? 'md:w-20' : 'md:w-[250px]'
                        }`}
                    >
                        <Sidebar isCollapsed={isCollapsed} />

                        <button
                            onClick={toggleNavbar}
                            className={`absolute bg-white shadow-lg p-1 rounded-lg top-10 z-30 -right-4 border`}
                        >
                            {isCollapsed ? <ChevronRight className='w-4 h-4' /> : <ChevronLeft className='w-4 h-4' />}
                        </button>
                    </div>

                    {/* Content Area */}
                    <div
                        className={`flex flex-col flex-1 transition-all ease-in-out duration-300 min-h-full ${
                            isCollapsed ? 'md:pl-20' : 'md:pl-[250px]'
                        }`}
                    >
                        {pagesToHideHeader || formsPage ? null : <Header isCollapsed={isCollapsed} />}

                        {isDemoUser && (
                            <div className='bg-primary-main h-10 flex items-center justify-center gap-2'>
                                <p className='text-sm text-white'>
                                    You're currently using a demo account. Upgrade to enjoy a full package with our
                                    14-day free trial!
                                </p>
                                <Sparkle color='white' />
                                <Button
                                    onClick={() => {
                                        navigate(`/dashboard/settings/billing`)
                                    }}
                                    className='h-6'
                                    size='sm'
                                    variant='outline'
                                >
                                    Start Here
                                </Button>
                            </div>
                        )}
                        <div
                            className={`w-full h-full overflow-x-hidden overflow-auto ${pagesToHideHeader ? '' : 'mt-[64px]'}`}
                        >
                            <FileContextProvider>
                                <SequenceContextProvider>
                                    <Outlet />
                                </SequenceContextProvider>
                            </FileContextProvider>
                        </div>
                    </div>
                    <AgentModal />
                </div>
        </CommandPaletteProvider>
    )
}

export default DashboardLayout
